import {
  firmOveragePages,
  firmPagePool,
  firmPlanCopy,
  firmPlanIds,
  firmPricing,
  formatUsdCents,
  isFirmPlanId,
  type FirmPlanId,
} from "./firm-billing.js"

/** The page plans: a monthly page tier at a flat price. */
export const pagePlanIds = ["BASIC", "PRO", "BIZ", "BIGTIME"] as const

export type PagePlanId = (typeof pagePlanIds)[number]

/** Every plan id a subscription row can hold: the page plans, then the firm
 * plans (billed per active client, see firm-billing.ts). */
export const subscriptionPlanIds = [...pagePlanIds, ...firmPlanIds] as const

export type SubscriptionPlanId = (typeof subscriptionPlanIds)[number]

export function isPagePlanId(planId: SubscriptionPlanId): planId is PagePlanId {
  return (pagePlanIds as readonly string[]).includes(planId)
}

/**
 * Array order encodes the page-tier ranking: BASIC < PRO < BIZ < BIGTIME.
 * Firm plans are not on that ladder, so any comparison with one is false.
 */
export function isPlanAtLeast(
  plan: SubscriptionPlanId,
  minimum: SubscriptionPlanId
): boolean {
  if (!isPagePlanId(plan) || !isPagePlanId(minimum)) {
    return false
  }

  return pagePlanIds.indexOf(plan) >= pagePlanIds.indexOf(minimum)
}

export const subscriptionBillingPeriods = ["monthly", "yearly"] as const

export type SubscriptionBillingPeriod =
  (typeof subscriptionBillingPeriods)[number]

export const contactSalesPrice = "Contact Sales" as const

export type SubscriptionPlanPrice = number | typeof contactSalesPrice

/** A selectable page-volume step within a plan. */
export interface SubscriptionPlanTier {
  /** Pages included per billing period at this step. */
  pages: number
  /** Price shown per month; yearly tiers show the effective monthly price. */
  price: number
}

export interface SubscriptionPlan {
  id: SubscriptionPlanId
  period: SubscriptionBillingPeriod
  name: string
  description: string
  /** Price of the default (first) tier. Kept so callers that predate page
   * tiers keep working; the tier list is the real source of price. */
  price: SubscriptionPlanPrice
  /** Pages included by the default tier. null = unlimited / contact sales. */
  pagesIncluded: number | null
  /** Selectable page-volume steps, cheapest first. Empty on contact-sales plans. */
  tiers: readonly SubscriptionPlanTier[]
  /** Team seats included. null = unlimited. */
  seatsIncluded: number | null
  /** Workspaces included. null = unlimited. */
  workspacesIncluded: number | null
  features: string[]
}

export function isContactSalesPlan(plan: SubscriptionPlan): boolean {
  return plan.price === contactSalesPrice
}

/** Plans that carry page tiers and a Stripe price. BIGTIME is contact-sales. */
export const paidPlanIds = ["BASIC", "PRO", "BIZ"] as const

export type PaidPlanId = (typeof paidPlanIds)[number]

export function isPaidPlanId(planId: SubscriptionPlanId): planId is PaidPlanId {
  return new Set<string>(paidPlanIds).has(planId)
}

/**
 * Page-volume steps per plan, cheapest first. Index 0 is what a plan defaults
 * to when nothing else is specified.
 *
 * These are deliberately the same steps a customer sees on the pricing slider,
 * so a tier is identified by its page count everywhere: in the URL, on the
 * subscription row, and in the Stripe price lookup. Adding a step here without
 * adding the matching Stripe price ids will make that step un-buyable rather
 * than mis-priced, which is the failure we want.
 */
export const planPageTiers = {
  BASIC: [60, 120, 300],
  PRO: [640, 1000, 1500],
  BIZ: [2000, 3000, 5000],
} as const satisfies Record<PaidPlanId, readonly number[]>

/** Monthly list price per step, aligned by index with `planPageTiers`. */
const monthlyTierPrices = {
  BASIC: [25, 33, 65],
  PRO: [135, 185, 255],
  BIZ: [340, 509, 765],
} satisfies Record<PaidPlanId, readonly number[]>

/**
 * Effective monthly price when billed annually, aligned by index with
 * `planPageTiers`. Roughly 30% under the monthly price at every step; the
 * amount Stripe actually charges is this times twelve.
 */
const yearlyTierPrices = {
  BASIC: [17, 23, 45],
  PRO: [95, 129, 179],
  BIZ: [239, 355, 535],
} satisfies Record<PaidPlanId, readonly number[]>

/** The page-volume steps offered for a plan + period, cheapest first. */
export function planTiersFor(
  planId: SubscriptionPlanId,
  period: SubscriptionBillingPeriod = "monthly"
): readonly SubscriptionPlanTier[] {
  if (!isPaidPlanId(planId)) {
    return []
  }

  const prices =
    period === "yearly" ? yearlyTierPrices[planId] : monthlyTierPrices[planId]

  return planPageTiers[planId].map((pages, index) => ({
    pages,
    price: prices[index],
  }))
}

/** The page count a plan defaults to when a caller does not name a tier. */
export function defaultTierPagesFor(planId: SubscriptionPlanId): number | null {
  return isPaidPlanId(planId) ? planPageTiers[planId][0] : null
}

/**
 * Resolve a tier by its page count. Returns null when the plan does not offer
 * that step, which callers must treat as "not buyable" rather than falling back
 * to a different price than the customer was shown.
 */
export function findPlanTier(
  planId: SubscriptionPlanId,
  pages: number,
  period: SubscriptionBillingPeriod = "monthly"
): SubscriptionPlanTier | null {
  return planTiersFor(planId, period).find((t) => t.pages === pages) ?? null
}

/** Every (plan, tier, period) combination that needs a Stripe price. */
export function allPlanTierCombinations(): {
  planId: PaidPlanId
  period: SubscriptionBillingPeriod
  pages: number
  price: number
}[] {
  const out: {
    planId: PaidPlanId
    period: SubscriptionBillingPeriod
    pages: number
    price: number
  }[] = []

  for (const planId of paidPlanIds) {
    for (const period of subscriptionBillingPeriods) {
      for (const tier of planTiersFor(planId, period)) {
        out.push({ planId, period, pages: tier.pages, price: tier.price })
      }
    }
  }

  return out
}

/**
 * Feature lists name only capabilities that exist in the product. Retention
 * windows, SSO, an SLA, a priority queue and seat caps were removed in the
 * page-tier repricing because nothing enforces them; do not add a bullet here
 * without the code to back it.
 */
const planCopy = {
  BASIC: {
    name: "Starter",
    description: "For a business with a small monthly volume.",
    features: [
      "Branded client portal",
      "Excel, CSV, QBO, and JSON exports",
      "QuickBooks file exports (CSV and QBO)",
      "Balance reconciliation",
      "API access and the open-source SDK",
      "Unlimited users",
      "Email support",
    ],
  },
  PRO: {
    name: "Pro",
    description: "For a business that sends its documents to QuickBooks Online.",
    features: [
      "Everything in Starter, plus:",
      "Send to QuickBooks Online",
      "Webhooks",
      "Batch extraction (up to 20 files)",
      "Automations",
      "Email forwarding to your inbox",
    ],
  },
  BIZ: {
    name: "Business",
    description: "For teams with serious monthly volume.",
    features: [
      "Everything in Pro, plus:",
      "Team collaboration",
      "Priority support",
    ],
  },
  BIGTIME: {
    name: "Enterprise",
    // No white-label or on-premise: nothing in the product does either.
    // What stays is work the team does by hand for a contract.
    description: "For organizations that need custom volume or integrations.",
    features: [
      "Everything in Business, plus:",
      "Custom integrations",
      "Onboarding support",
      "Custom annual pricing",
    ],
  },
} satisfies Record<
  PagePlanId,
  { name: string; description: string; features: string[] }
>

function buildPlans(
  period: SubscriptionBillingPeriod
): readonly SubscriptionPlan[] {
  return pagePlanIds.map((id) => {
    const copy = planCopy[id]
    const tiers = planTiersFor(id, period)
    const features =
      period === "yearly"
        ? ["Save around 30% with annual billing", ...copy.features]
        : copy.features

    return {
      id,
      period,
      name: copy.name,
      description: copy.description,
      price: tiers.length > 0 ? tiers[0].price : contactSalesPrice,
      pagesIncluded: tiers.length > 0 ? tiers[0].pages : null,
      tiers,
      // Users are not metered and nothing enforces a seat cap, so every plan is
      // unlimited rather than claiming a limit we do not apply.
      seatsIncluded: null,
      workspacesIncluded: null,
      features,
    }
  })
}

const monthlyPlans: readonly SubscriptionPlan[] = buildPlans("monthly")
const yearlyPlans: readonly SubscriptionPlan[] = buildPlans("yearly")

/**
 * What a firm plan's card says it includes, on the pricing page and on the
 * plan cards in the app. Both read this, so they cannot list different things.
 * Each capability line resolves through `deriveEntitlements`, the policy the
 * API enforces, so a card cannot promise what the plan does not get.
 */
export function firmPlanAllowance(id: FirmPlanId): string {
  return id === "FIRM_FREE"
    ? `Up to ${firmPricing.freeActiveClients} clients and ${firmPricing.freePageCap} pages a month`
    : `${firmPricing.includedPagesPerActiveClient} pages per client, shared across your firm`
}

/** The lines under a firm plan's allowance. See `firmPlanAllowance`. */
export function firmPlanFeatures(id: FirmPlanId): string[] {
  const can = deriveEntitlements({
    planId: id,
    status: "active",
    trialEndsAt: null,
    pagesUsed: 0,
    activeClients: 1,
    now: new Date(0),
  })
  const overage = formatUsdCents(firmPricing.overagePerPageCents)

  return [
    ...(id === "FIRM" ? [`${overage} for each page after that`] : []),
    ...(can.canUsePortal ? ["Client portal and requests"] : []),
    ...(can.canPostToDestinations ? ["Send to QuickBooks Online"] : []),
    ...(can.canExportAccounting ? ["QuickBooks CSV and QBO exports"] : []),
    "Excel, CSV, and JSON exports",
    "Unlimited users",
  ]
}

/**
 * Firm plans in the same shape, so the usage API and the plan name in emails
 * and audit lines work for them unchanged. `price` is per active client, and
 * `pagesIncluded` is the free tier's cap or null on the paid plan, whose pool
 * grows with its active clients (see `firmPagePool`). The yearly period is the
 * 12-month term, billed monthly.
 */
function buildFirmPlan(
  id: FirmPlanId,
  period: SubscriptionBillingPeriod
): SubscriptionPlan {
  const copy = firmPlanCopy[id]
  const price =
    id === "FIRM_FREE"
      ? 0
      : period === "yearly"
        ? firmPricing.termPerActiveClientMonthlyUsd
        : firmPricing.perActiveClientMonthlyUsd

  return {
    id,
    period,
    name: copy.name,
    description: copy.description,
    price,
    pagesIncluded: id === "FIRM_FREE" ? firmPricing.freePageCap : null,
    tiers: [],
    seatsIncluded: null,
    workspacesIncluded: null,
    features: firmPlanFeatures(id),
  }
}

/** Page plans only. Firm plans are read with `getSubscriptionPlan`. */
export const subscriptionPlans = {
  monthly: monthlyPlans,
  yearly: yearlyPlans,
} satisfies Record<SubscriptionBillingPeriod, readonly SubscriptionPlan[]>

/** Lifecycle of an organization's subscription. Drives metering + entitlements. */
export const subscriptionStatuses = [
  "trialing",
  "active",
  "past_due",
  "canceled",
] as const

export type SubscriptionStatus = (typeof subscriptionStatuses)[number]

/** Length of the auto-assigned Starter trial granted to a brand-new org. */
export const trialDurationDays = 14

/**
 * A trial has two allowances, and which one it holds depends on whether it has
 * been unlocked (by sharing LedgerBox or by upgrading).
 *
 * The locked allowance is deliberately small: enough to prove the extraction is
 * real on one document, not enough to finish a quarter's bookkeeping and leave.
 * There is no separate gate on viewing, exporting or posting, because the cap
 * already gates all three: you cannot review rows from a document that was
 * never processed. One lever, not four.
 */
export const trialPagesInitial = 10

/** The trial allowance once unlocked. This is the number every trial used to
 * start at, unconditionally. */
export const trialPagesUnlocked = 200

/**
 * Ceiling on the first-document grace. A locked trial may run one batch past
 * the 10 page allowance up to this many pages, so that a first upload of a real
 * bank statement finishes instead of being refused whole.
 *
 * Without this the feature defeats itself. The pre-flight refuses a batch that
 * would land past the cap rather than processing part of it, and a statement is
 * routinely more than 10 pages, so a typical first upload would be rejected
 * having produced not one extracted row. That does not demonstrate the product,
 * and a user who has seen nothing has no reason to share anything.
 */
export const trialFirstDocumentPages = 30

/**
 * How a trial came to be unlocked. Recorded rather than reduced to a boolean
 * because it is the only way to learn which of the offers is worth keeping:
 * four mechanisms that all set the same flag would ship four guesses and
 * measure none of them.
 */
// There is no `upgrade` member, though upgrading is the other way out of the
// gate. Buying a plan moves the organization off `trialing` altogether, and the
// trial allowance stops being consulted at all, so there is nothing to record.
export const trialUnlockMethods = [
  "team_invite",
  "social_share",
  // Retired as a claimable method 2026-08: copying a link proved nothing was
  // shared. Stays in the enum because unlocked trials recorded it.
  "referral_link",
  // Writing a G2 review. Self-serve since 2026-08-21; see
  // `selfServeTrialUnlockMethods` below for why it is taken at its word.
  "review",
  // An operator handing back an unlocked trial from the admin console. Someone
  // resetting a trial for a customer is doing them a favour, and a favour that
  // arrives with a gate on it is not one.
  "operator_grant",
  // Trials that were already running when the gate shipped. They were promised
  // 200 pages with nothing asked in return, and taking that back mid-trial
  // would be a broken promise for the sake of a week: the whole grandfathered
  // set expires within 14 days of the migration.
  "grandfathered",
] as const

export type TrialUnlockMethod = (typeof trialUnlockMethods)[number]

/**
 * The methods a user may claim for themselves through the API. `upgrade` is
 * recorded by subscription sync rather than claimed, and `operator_grant` and
 * `grandfathered` are things done to a trial rather than by its owner.
 * `referral_link` is no longer claimable: copying a link demonstrated nothing,
 * so the dialog now points that user at a plan instead.
 *
 * `review` became self-serve on 2026-08-21. It used to need an operator to
 * confirm it in the admin console, which meant nobody was ever offered it, so
 * in practice it was not a way out of the gate at all. It is honour based for
 * exactly the reason `social_share` is: G2 does not tell us who wrote what, a
 * review sits in moderation for days, and gating an allowance on an event we
 * cannot observe means gating it on nothing. The ceiling on a fabricated claim
 * is a 200 page trial that expires in 14 days, which is what every trial used
 * to be handed unconditionally.
 *
 * Order is the order the dialog offers them, and it is deliberate: invite
 * first, because it is the one with a second person at the end of it.
 */
export const selfServeTrialUnlockMethods = [
  "team_invite",
  "social_share",
  "review",
] as const satisfies readonly TrialUnlockMethod[]

export type SelfServeTrialUnlockMethod =
  (typeof selfServeTrialUnlockMethods)[number]

/** A trial is recorded against the Starter (BASIC) plan id until it converts. */
export const trialPlanId: SubscriptionPlanId = "BASIC"

/** Billing period a trial/subscription is created against by default. */
export const defaultBillingPeriod: SubscriptionBillingPeriod = "monthly"

/** Resolve a plan definition by id + period. Throws on an unknown plan. */
export function getSubscriptionPlan(
  planId: SubscriptionPlanId,
  period: SubscriptionBillingPeriod = defaultBillingPeriod
): SubscriptionPlan {
  if (isFirmPlanId(planId)) {
    return buildFirmPlan(planId, period)
  }

  const plan = subscriptionPlans[period].find(
    (candidate) => candidate.id === planId
  )

  if (!plan) {
    throw new Error(`Unknown subscription plan ${planId} for ${period} period.`)
  }

  return plan
}

/**
 * Pages included for a plan/period. `null` means unlimited (contact sales).
 *
 * `tierPages` is the page step the subscription actually bought. It is honoured
 * only when the plan still offers that step; an unrecognised value falls back to
 * the plan's default tier rather than granting an allowance we never sold. That
 * matters when a tier is retired: the subscription keeps billing at its Stripe
 * price, and we would rather under-grant and hear about it than silently hand
 * out 5,000 pages.
 */
export function pagesIncludedFor(
  planId: SubscriptionPlanId,
  period: SubscriptionBillingPeriod = defaultBillingPeriod,
  tierPages?: number | null
): number | null {
  if (tierPages !== undefined && tierPages !== null) {
    const tier = findPlanTier(planId, tierPages, period)
    if (tier) {
      return tier.pages
    }
  }

  return getSubscriptionPlan(planId, period).pagesIncluded
}

/** Effective allowance for an org's current state: the trial allowance while
 * trialing (which of the two depends on whether the trial has been unlocked),
 * otherwise the included pages of the tier it bought. */
export function effectivePagesIncluded(
  planId: SubscriptionPlanId,
  status: SubscriptionStatus,
  period: SubscriptionBillingPeriod = defaultBillingPeriod,
  tierPages?: number | null,
  trialUnlocked = false,
  activeClients = 0
): number | null {
  if (isFirmPlanId(planId)) {
    return firmPagePool(planId, activeClients)
  }

  if (status === "trialing") {
    return trialUnlocked ? trialPagesUnlocked : trialPagesInitial
  }

  return pagesIncludedFor(planId, period, tierPages)
}

/** The span a plan's page allowance is measured over. */
export type AllowanceWindow = { start: Date; end: Date }

// Adding a month to a date that has no counterpart in the shorter month, e.g.
// the 31st in February, must clamp to that month's last day. Letting the Date
// constructor roll it into the next month would open a second window inside the
// same month and hand out the allowance twice.
const addMonthsClamped = (from: Date, months: number): Date => {
  const day = from.getUTCDate()
  const target = new Date(from)

  target.setUTCDate(1)
  target.setUTCMonth(target.getUTCMonth() + months)

  const lastDayOfTargetMonth = new Date(
    Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)
  ).getUTCDate()

  target.setUTCDate(Math.min(day, lastDayOfTargetMonth))

  return target
}

/**
 * The window a subscription's page allowance is counted over.
 *
 * Plans sell a monthly allowance on both billing periods: a tier carries the
 * same `pages` whether it is billed monthly or yearly, and only the price
 * differs. A yearly subscription's Stripe period is nevertheless a whole year,
 * so measuring usage over it gave a yearly subscriber one month's pages to last
 * twelve — a twelfth of what the plan cards advertise, and worse than the
 * monthly customers who pay more in total. Yearly usage is therefore counted
 * over the current month of the subscription year, anchored on the day the
 * period began so the reset lands on the customer's own date rather than the
 * first of the calendar month.
 *
 * Monthly subscriptions already bill on the window they are measured over, so
 * their Stripe period is used unchanged.
 */
export function allowanceWindow(input: {
  billingPeriod: SubscriptionBillingPeriod
  currentPeriodStart: Date
  /** Stripe's own period end, used as-is on a monthly plan. */
  currentPeriodEnd?: Date | null
  now: Date
}): AllowanceWindow {
  if (input.billingPeriod !== "yearly") {
    return {
      start: input.currentPeriodStart,
      // Stripe's date is authoritative when we have it; deriving one would
      // drift from the day the customer is actually billed.
      end:
        input.currentPeriodEnd ?? addMonthsClamped(input.currentPeriodStart, 1),
    }
  }

  // An unparseable date would make every comparison below false. Fall back to
  // the Stripe period rather than searching for a window that cannot exist.
  if (
    !Number.isFinite(input.currentPeriodStart.getTime()) ||
    !Number.isFinite(input.now.getTime())
  ) {
    return {
      start: input.currentPeriodStart,
      end: input.currentPeriodEnd ?? input.currentPeriodStart,
    }
  }

  return anchoredMonthlyWindow(input.currentPeriodStart, input.now)
}

/**
 * The month-long window that contains `now`, stepped in whole months from
 * `anchor` so every boundary falls on the anchor's day of the month.
 */
export function anchoredMonthlyWindow(
  anchor: Date,
  now: Date
): AllowanceWindow {
  // Count the months directly rather than stepping one at a time: a stale
  // anchor (a subscription whose price we no longer recognise is never
  // rewritten by the webhook sync, so its period start can be years old) would
  // otherwise cost an iteration per month on every entitlement check, and this
  // runs on the per-run budget gate.
  const monthsApart =
    (now.getUTCFullYear() - anchor.getUTCFullYear()) * 12 +
    (now.getUTCMonth() - anchor.getUTCMonth())

  // The calendar-month difference overshoots by one until the anniversary day
  // is reached, and clamping a month-end anchor can leave the boundary later
  // than the arithmetic suggests, so settle it by comparison.
  let months = Math.max(0, monthsApart)

  while (months > 0 && addMonthsClamped(anchor, months) > now) {
    months -= 1
  }

  // Stepping whole months from the origin, rather than accumulating from the
  // previous window, is what keeps every boundary on the anniversary day and
  // stops short months from drifting the reset across the year.
  return {
    start: addMonthsClamped(anchor, months),
    end: addMonthsClamped(anchor, months + 1),
  }
}

/**
 * The billing month an organization's usage and active clients are counted
 * over, for any plan.
 *
 * Firm plans are always monthly, whatever the billing period says: the
 * 12-month term is billed monthly on the meter, and the free tier has no
 * Stripe period at all. Using the yearly Stripe period here would repeat the
 * annual allowance bug (one month's pool stretched over a year), so a firm
 * plan always steps whole months from its anchor. The free tier anchors on
 * the subscription row's creation, the paid plan on its Stripe period.
 *
 * Page plans keep `allowanceWindow`.
 */
export function billingWindowFor(input: {
  planId: SubscriptionPlanId
  billingPeriod: SubscriptionBillingPeriod
  currentPeriodStart: Date | null
  currentPeriodEnd?: Date | null
  /** When the subscription row was created: the anchor of last resort. */
  createdAt: Date
  now: Date
}): AllowanceWindow {
  const anchor = input.currentPeriodStart ?? input.createdAt

  if (isFirmPlanId(input.planId)) {
    // A stale anchor (a canceled subscription's last Stripe period) is fine:
    // stepping whole months from it still gives a window one month long.
    return Number.isFinite(anchor.getTime()) &&
      Number.isFinite(input.now.getTime())
      ? anchoredMonthlyWindow(anchor, input.now)
      : { start: anchor, end: addMonthsClamped(anchor, 1) }
  }

  return allowanceWindow({
    billingPeriod: input.billingPeriod,
    currentPeriodStart: anchor,
    currentPeriodEnd: input.currentPeriodEnd,
    now: input.now,
  })
}

/** Team seats included for a plan. `null` = unlimited. */
export function seatsIncludedFor(
  planId: SubscriptionPlanId,
  period: SubscriptionBillingPeriod = defaultBillingPeriod
): number | null {
  return getSubscriptionPlan(planId, period).seatsIncluded
}

/** Workspaces included for a plan. `null` = unlimited. */
export function workspacesIncludedFor(
  planId: SubscriptionPlanId,
  period: SubscriptionBillingPeriod = defaultBillingPeriod
): number | null {
  return getSubscriptionPlan(planId, period).workspacesIncluded
}

export type EntitlementBlockReason =
  | "trial_expired"
  | "quota_exceeded"
  | "subscription_required"

export type Entitlements = {
  /** Whether new processing may be enqueued. */
  canProcess: boolean
  /** Whether QuickBooks/accounting exports are permitted (paid + active only). */
  canExportAccounting: boolean
  /** Whether API keys may authenticate requests (active Pro and up, or trialing). */
  canUseApi: boolean
  /** Whether webhook endpoints may be managed and receive deliveries (active Pro and up, or trialing). */
  canUseWebhooks: boolean
  /** Whether direct posting to accounting destinations is permitted (active Pro and up, or trialing). */
  canPostToDestinations: boolean
  /** Whether forwarded email is ingested for this org (active Pro and up, or trialing). */
  canUseEmailForwarding: boolean
  /** Whether the agents may run (every paid plan, active only). One flag for
   * every kind, not one per kind: the agents are one upgrade story, and a
   * per-kind matrix would drift from it. Trials see the cards with the agents
   * locked, so evaluating what they would pay for here means watching
   * automation act on real approvals, which is exactly the thing a trial has
   * not earned yet. */
  canUseAgents: boolean
  /** Whether clients may use the hosted portal. Page caps make it read-only;
   * only an expired trial or canceled subscription removes access. */
  canUsePortal: boolean
  /** Whether usage has exceeded the included allowance (overage accruing). */
  isOverIncluded: boolean
  /** Pages left before overage; null when the allowance is unlimited. */
  pagesRemaining: number | null
  /** Billable pages beyond the included allowance (0 on trials — hard-capped). */
  overagePages: number
  /** Estimated overage cost this period, in cents (0 on trials). */
  overageCents: number
  /** True when exceeding the allowance is refused rather than billed as
   * overage (trials). Drives the pre-flight page budget check. */
  hardPageCap: boolean
  /** Whether this trial has been unlocked to the full allowance. Always false
   * off a trial, where the concept does not apply: read it only alongside
   * `trialing` to decide whether to offer the unlock. */
  trialUnlocked: boolean
  /** Whether the locked trial may still overrun its allowance once, for a
   * single first document. Consumed by the pre-flight check; surfaced so the
   * upload dialog can promise the first document rather than a page count. */
  firstDocumentGrace: boolean
  /** Why processing is blocked, or null when allowed. */
  blockReason: EntitlementBlockReason | null
}

export type DeriveEntitlementsInput = {
  planId: SubscriptionPlanId
  billingPeriod?: SubscriptionBillingPeriod
  status: SubscriptionStatus
  trialEndsAt: Date | null
  pagesUsed: number
  /**
   * The page step this subscription bought, from the subscription row. Omitted
   * or null means the plan's default (cheapest) tier.
   */
  includedPages?: number | null
  /**
   * Unspent operator-granted pages, which raise this period's cap on top of the
   * plan allowance. Defaults to 0 so every existing caller is unchanged.
   */
  bonusPagesRemaining?: number
  /**
   * Whether this trial has been unlocked to the full allowance, by sharing
   * LedgerBox or by upgrading. Defaults to false, which is the state every
   * brand-new organization starts in. Ignored off a trial.
   */
  trialUnlocked?: boolean
  /**
   * Active clients this billing month. Read only on firm plans, where it sets
   * the size of the page pool. Defaults to 0.
   */
  activeClients?: number
  now: Date
}

// Pure policy mapping a subscription + usage snapshot to what an org may do:
// - active (paid): hard-capped at the tier it bought; accounting exports
//   allowed; integrations (API, webhooks, posting) from Pro up.
// - trialing: hard-capped at the trial allowance and at expiry (D7/D10). The
//   allowance itself is 10 pages until the trial is unlocked by a share or an
//   upgrade, then the full 200; the cap is the only thing the unlock moves,
//   because nothing else needs gating once processing is gated. Integrations
//   are included so a trial can evaluate them, until the trial expires;
//   accounting exports stay excluded (D12) whether unlocked or not, so a live
//   trial can post to QuickBooks but cannot download the file. Sharing buys
//   pages, not the deliverable. That split is deliberate, not an oversight.
// - past_due: grace on processing; accounting export and integrations gated
//   until active again.
// - canceled/other: blocked until a subscription exists.
//
// Every capped plan now stops at its allowance rather than metering an overage.
// A customer who needs more pages moves up a step on the slider, which is the
// whole point of selling tiers: the upgrade is the product, not a line item on
// a surprise invoice.
export function deriveEntitlements(
  input: DeriveEntitlementsInput
): Entitlements {
  if (isFirmPlanId(input.planId)) {
    return deriveFirmEntitlements({ ...input, planId: input.planId })
  }

  const trialUnlocked =
    input.status === "trialing" && input.trialUnlocked === true
  const planPagesIncluded = effectivePagesIncluded(
    input.planId,
    input.status,
    input.billingPeriod,
    input.includedPages,
    trialUnlocked
  )
  // The cap an org is actually held to: its plan allowance plus any unspent
  // operator grant. Unlimited plans stay unlimited.
  const pagesIncluded =
    planPagesIncluded === null
      ? null
      : planPagesIncluded + Math.max(0, input.bonusPagesRemaining ?? 0)
  const pagesRemaining =
    pagesIncluded === null ? null : Math.max(0, pagesIncluded - input.pagesUsed)
  const isOverIncluded =
    pagesIncluded !== null && input.pagesUsed > pagesIncluded
  const atOrOverCap = pagesIncluded !== null && input.pagesUsed >= pagesIncluded

  // No plan meters an overage any more. The fields stay on the shape so the
  // usage API, the meter and the admin views keep their contract, but they are
  // always zero: exceeding the allowance stops processing and offers the next
  // tier instead of billing for it.
  const usage = {
    isOverIncluded,
    pagesRemaining,
    overagePages: 0,
    overageCents: 0,
    hardPageCap: pagesIncluded !== null,
    trialUnlocked,
    // Held only by a locked trial that has not processed anything yet, so the
    // grace can be spent on exactly one batch. It is keyed on posted usage
    // being zero rather than on a document count because the ledger is what
    // the cap is enforced against; concurrent uploads that all arrive before
    // anything posts are bounded by the ceiling the pre-flight applies.
    firstDocumentGrace:
      input.status === "trialing" && !trialUnlocked && input.pagesUsed === 0,
  }

  // A trial that has run past its end date is over, and nothing else ever moves
  // an abandoned trial off `trialing`: only Stripe sync writes `status`, and it
  // never runs for an org that skipped checkout. Without this the org would hold
  // machine access forever.
  const trialExpired =
    input.status === "trialing" &&
    input.trialEndsAt !== null &&
    input.now.getTime() > input.trialEndsAt.getTime()

  // Integration capabilities (API keys, webhooks, direct posting). A trial gets
  // them so it can evaluate what it would be paying for, and none of the three
  // can process a page outside the same capped budget. past_due is still
  // excluded so a lapsed card cannot keep machine access alive.
  //
  // The page cap deliberately does not revoke them: a capped trial keeps its
  // endpoint list readable and manageable while processing is paused, and every
  // path that would actually spend a page is gated on canProcess independently.
  // Expiry is different, because a trial past its end date is not paused, it is
  // finished.
  const liveSubscription =
    (input.status === "trialing" && !trialExpired) || input.status === "active"

  const hasProIntegrations =
    liveSubscription &&
    (input.status === "trialing" || isPlanAtLeast(input.planId, "PRO"))

  // The API is deliberately wider than the other two. Webhooks and posting are
  // things we do on a customer's behalf and are worth a plan; calling the API is
  // how a customer reaches the product at all, and every request already spends
  // metered pages through the same ledger a dashboard upload does. Gating it on
  // PRO meant a paying BASIC customer could not use the thing they pay for, and
  // meant an SDK quickstart ended at a paywall for everyone whose trial lapsed
  // while they were still reading it.
  const integrations = {
    canUseApi: liveSubscription,
    canUseWebhooks: hasProIntegrations,
    canPostToDestinations: hasProIntegrations,
    // Forwarding is a Pro feature (billing.ts plan copy sells it there), and a
    // live trial gets it for the same reason it gets the other integrations:
    // it is evaluating what it would pay for, inside the same page budget.
    canUseEmailForwarding: hasProIntegrations,
    // Every paid plan, active only. Not `liveSubscription`: a trial reads the
    // agent cards but cannot run them, and a lapsed card pauses them the way
    // it pauses the other machine access. The engine re-reads this on every
    // pass, so a downgrade stops the agents without a cleanup job.
    canUseAgents: input.status === "active",
    // A failed card keeps the client handoff surface alive while staff resolve
    // billing. Uploads are held and cannot spend pages; only a finished trial
    // or canceled subscription turns the portal read-only.
    canUsePortal: liveSubscription || input.status === "past_due",
  }

  if (input.status === "active") {
    // Paid plans now stop at the allowance too. Exports stay available at the
    // cap on purpose: the customer has already paid for those pages and
    // withholding the output would be taking back work they bought. Only new
    // processing pauses, until they move up a tier or the period rolls over.
    return {
      ...usage,
      ...integrations,
      canProcess: !atOrOverCap,
      canExportAccounting: true,
      blockReason: atOrOverCap ? "quota_exceeded" : null,
    }
  }

  if (input.status === "trialing") {
    const blockReason: EntitlementBlockReason | null = trialExpired
      ? "trial_expired"
      : atOrOverCap
        ? "quota_exceeded"
        : null

    return {
      ...usage,
      ...integrations,
      canProcess: blockReason === null,
      canExportAccounting: false,
      blockReason,
    }
  }

  if (input.status === "past_due") {
    // Grace on the failed payment, but not on the page cap: a lapsed card is a
    // billing problem, being out of pages is a plan problem, and the second one
    // is not forgiven by the first.
    return {
      ...usage,
      ...integrations,
      canProcess: !atOrOverCap,
      canExportAccounting: false,
      blockReason: atOrOverCap ? "quota_exceeded" : null,
    }
  }

  return {
    ...usage,
    ...integrations,
    canProcess: false,
    canExportAccounting: false,
    blockReason: "subscription_required",
  }
}

// Firm plans.
// - FIRM_FREE: a hard cap of 30 pages a month for the whole firm, like a
//   trial's cap but without an end date. It gets what a live trial gets
//   (portal, API, webhooks, posting, forwarding) and, like a trial, neither
//   QuickBooks file exports nor the agents. The 3-client limit is not a
//   capability; it is checked where a firm action would activate a client
//   (`evaluateClientActivation`).
// - FIRM active: the pool is 50 pages per active client, and pages past it
//   are billed as overage rather than refused. Everything is included.
// - FIRM past_due: the same grace as a page plan. Extraction continues and
//   accrues overage; exports and integrations wait for the card.
// - FIRM canceled: back to FIRM_FREE.
function deriveFirmEntitlements(
  input: DeriveEntitlementsInput & { planId: FirmPlanId }
): Entitlements {
  const onFree = input.planId === "FIRM_FREE" || input.status === "canceled"
  const planId: FirmPlanId = onFree ? "FIRM_FREE" : "FIRM"
  const pool = firmPagePool(planId, input.activeClients ?? 0)
  const pagesIncluded = pool + Math.max(0, input.bonusPagesRemaining ?? 0)
  const pagesRemaining = Math.max(0, pagesIncluded - input.pagesUsed)
  const overagePages = onFree
    ? 0
    : firmOveragePages(input.pagesUsed, pagesIncluded)
  const atOrOverCap = input.pagesUsed >= pagesIncluded

  const usage = {
    isOverIncluded: input.pagesUsed > pagesIncluded,
    pagesRemaining,
    overagePages,
    overageCents: overagePages * firmPricing.overagePerPageCents,
    hardPageCap: onFree,
    trialUnlocked: false,
    firstDocumentGrace: false,
  }

  if (onFree) {
    return {
      ...usage,
      canUseApi: true,
      canUseWebhooks: true,
      canPostToDestinations: true,
      canUseEmailForwarding: true,
      canUseAgents: false,
      canUsePortal: true,
      canProcess: !atOrOverCap,
      canExportAccounting: false,
      blockReason: atOrOverCap ? "quota_exceeded" : null,
    }
  }

  const active = input.status === "active" || input.status === "trialing"

  return {
    ...usage,
    canUseApi: active,
    canUseWebhooks: active,
    canPostToDestinations: active,
    canUseEmailForwarding: active,
    canUseAgents: active,
    canUsePortal: true,
    canProcess: true,
    canExportAccounting: active,
    blockReason: null,
  }
}

export type PageBudgetVerdict = {
  allowed: boolean
  /** Why the pages were refused, or null when allowed. */
  reason: EntitlementBlockReason | null
}

/** Error code carried by the worker's failure callback when a run is refused
 * for exceeding the page budget. The callback repository maps it to the
 * document's `needs_credits` state; keep the two in sync. */
export const pageBudgetExceededErrorCode = "page_budget_exceeded" as const

// Pre-flight policy: may this org process `incomingPages` more pages right
// now? Blocked orgs refuse everything; every capped plan (trial and paid)
// refuses a batch that would land past its allowance; there is no overage.
// `incomingPages` should include pages already reserved by concurrent
// in-flight runs.
export function evaluatePageBudget(
  entitlements: Entitlements,
  incomingPages: number
): PageBudgetVerdict {
  if (!entitlements.canProcess) {
    return {
      allowed: false,
      reason: entitlements.blockReason ?? "quota_exceeded",
    }
  }

  if (
    entitlements.hardPageCap &&
    entitlements.pagesRemaining !== null &&
    incomingPages > entitlements.pagesRemaining
  ) {
    // One exception, and only one: a locked trial's first document is allowed
    // to overrun the 10 page allowance so it finishes, up to the ceiling. What
    // we are selling at this point is proof that the extraction works, and a
    // 14 page statement refused whole proves nothing. A second batch finds the
    // grace spent, because posted usage is no longer zero.
    if (
      entitlements.firstDocumentGrace &&
      incomingPages <= trialFirstDocumentPages
    ) {
      return { allowed: true, reason: null }
    }

    return { allowed: false, reason: "quota_exceeded" }
  }

  return { allowed: true, reason: null }
}

/** Share of the included allowance that trips the near-limit heads-up. */
export const nearPageLimitRatio = 0.9

// Whether posted usage has reached the warning threshold, on posted usage
// only: the same number the usage endpoint reports, not the pending-inclusive
// view the budget check works from. The heads-up email and the in-app banner
// both read this so they cannot disagree about when to speak up; the OCR
// worker stays the authoritative per-page enforcer via evaluatePageBudget.
export function isNearPageLimit(
  pagesUsed: number,
  pagesIncluded: number | null
): boolean {
  // Unlimited (Enterprise) and empty allowances have no fraction to be near.
  if (pagesIncluded === null || pagesIncluded <= 0) {
    return false
  }

  return pagesUsed / pagesIncluded >= nearPageLimitRatio
}
